<template>
  <div v-if="hasAccess(MARKETING_MATERIALS_ACCESS, MARKETING_MATERIALS_ACCESS_MANAGE)"
       class="marketing-materials widgetReal" ref="widgetReal">
    <h1>Real Widget</h1>
    <template v-if="pages && pages.length === 0">
      <notification-financing-page/>
    </template>
    <template v-else-if="pages && realCode">
      <GetCodeWidget
          :active="codeModalActive"
          :code="code"
          :email-sending="emailSending"
          :nameWidjet="nameWidget"
          :nameWidjetFull="nameWidgetFull"
          @submit="submit"
          @close="closeModal">
      </GetCodeWidget>

      <PageSelector />

      <div class="columns">
        <div class="column">
          <p>
            The Real Widget will enable your client to fill out an application
            for financing and receive offers from lenders seamlessly - right on your website.
            The widget will open with a click on the button that you can place anywhere
            on your website. Choose the color scheme for the widget and the button
            to fit your website style and embed the widget in your website.</p>
          <div class="choose">
            <p class="title">Choose widget color</p>
            <div class="chooseOptions">
              <button v-for="(option, key) in options" :key="key"
                      v-bind:class="{ active: selected === key }"
                      @click="optionClickHandler(key)">
                <span :style="{ background: getGradient(option.color1, option.color2) }"></span>
              </button>
            </div>
            <div class="buttons">
              <b-button
                  type="is-success"
                  @click.prevent="showWidgetRealModal"
                  :loading="codeLoading"
                  class="get-real-widget-code"
              >
                Get Widget Code
              </b-button>
            </div>
          </div>
        </div>
        <div class="column">
          <div id="wr-container" class="hidden"></div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import {
  realWidgetCode,
  realWidgetSendToEmail,
} from '@/api/widget';
import * as CONTRACTOR_ACTIONS from '@/store/modules/contractor/actions';
import * as MUTATIONS from '@/store/mutations';
import NotificationFinancingPage from '@/components/NotificationFinancingPage.vue';
import GetCodeWidget from '@/components/Modal/GetCodeWidget.vue';
import PageSelector from '@/views/MarketingMaterials/components/PageSelector.vue';

export default {
  name: 'RealWidget',
  components: {
    PageSelector,
    GetCodeWidget,
    NotificationFinancingPage,
  },
  computed: {
    ...mapState('contractor', { contractorId: (state) => state.id }),
    ...mapState('user', { userId: (state) => state.id }),
    ...mapGetters(['hasAccess']),
    ...mapGetters('contractor', ['selectedPage', 'pages']),
  },
  watch: {
    async selectedPage() {
      if (this.isActivated) {
        await this.loadScript();
      }
    },
  },
  data() {
    return {
      options: {
        default: {
          color1: '#68BA62',
          color2: '#1C418C',
          color3: '#FFFFFF',
        },
        'red/blue': {
          color1: '#B31417',
          color2: '#064C8F',
          color3: '#FFFFFF',
        },
        'orange/blue': {
          color1: '#FF531B',
          color2: '#054079',
          color3: '#FFFFFF',
        },
        'gold/black': {
          color1: '#BD921E',
          color2: '#3A3A37',
          color3: '#FFFFFF',
        },
        'yellow/purple': {
          color1: '#FFD200',
          color2: '#522762',
          color3: '#293033',
        },
      },
      selected: 'default',
      code: null,
      codeLoading: false,
      codeModalActive: false,
      emailSending: false,
      realCode: true,
      nameWidget: 'Real',
      nameWidgetFull: 'Real',
      script: null,
      html: null,
      previewScriptUrl: null,
      previewHtml: null,
      viewPreview: false,
      realPreviewScript: null,
      textButton: 'Apply now',
      widthBtn: '400',
      wrContainer: null,
      MARKETING_MATERIALS_ACCESS: { resource: 'marketing-materials', code: 'access' },
      MARKETING_MATERIALS_ACCESS_MANAGE: { resource: 'marketing-materials', code: 'manage_access' },
      isActivated: false,
    };
  },
  methods: {
    ...mapActions('contractor', { updPages: CONTRACTOR_ACTIONS.LOAD_PAGES }),
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),
    ...mapActions('contractor', { setSelectedPage: CONTRACTOR_ACTIONS.SET_SELECTED_PAGE }),

    getGradient(color1, color2) {
      return `linear-gradient(90deg, ${color1}, ${color1} 50%, ${color2} 50%)`;
    },
    optionClickHandler(key) {
      this.wrContainer.classList.add('hidden');
      if (key) {
        this.selected = key;
      }
      this.loadScript();
    },
    showWidgetRealModal() {
      this.openCodeModal();
    },
    closeModal() {
      this.tipModalActive = false;
      this.codeModalActive = false;
    },
    openCodeModal() {
      this.codeModalActive = true;
    },
    async loadScript() {
      if (this.contractorId === null || !this.selectedPage?.id) return;
      const formData = this.generateurl();
      await realWidgetCode(formData).then((response) => {
        this.script = response.data.script;
        this.html = response.data.html;
        const scrTag = document.createElement('script');
        scrTag.setAttribute('src', response.data.script);
        this.code = scrTag.outerHTML.replace(/amp;/g, '') + this.html;
        this.previewScriptUrl = response.data.previewScript;
        this.previewHtml = response.data.previewHtml;
        const realPreviewScript = document.createElement('script');
        realPreviewScript.setAttribute('src', this.previewScriptUrl);
        realPreviewScript.setAttribute('id', 'real-preview-script');
        document.head.appendChild(realPreviewScript);
        realPreviewScript.onload = () => {
          this.wrContainer.innerHTML = this.previewHtml;
          if (typeof window.getPreviewImageRealWidget === 'function') {
            // eslint-disable-next-line no-undef
            getPreviewImageRealWidget();
            this.wrContainer.classList.remove('hidden');
          }
        };
      });
    },
    generateurl() {
      const formData = new FormData();
      formData.set('userId', this.userId);
      formData.set('pageId', this.selectedPage.id);
      formData.set('color1', this.options[this.selected].color1.replace('#', '$'));
      formData.set('color2', this.options[this.selected].color2.replace('#', '$'));
      formData.set('color3', this.options[this.selected].color3.replace('#', '$'));
      formData.set('textButton', this.textButton);
      formData.set('widthbtn', this.widthBtn);
      return formData;
    },
    submit(email) {
      if (this.contractorId === null || !this.selectedPage?.id) return;
      this.emailSending = true;
      const formData = this.generateurl();
      formData.set('email', email);

      realWidgetSendToEmail(formData)
        .then((response) => {
          if (response.status === 200) {
            this.$buefy.toast.open({ message: 'Success!', type: 'is-success', duration: 3000 });
          }
        })
        .catch(() => this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger', duration: 3000 }))
        .finally(() => {
          this.emailSending = false;
        });
    },
  },
  deactivated() {
    this.isActivated = false;
  },
  async activated() {
    this.wrContainer = null;
    if (!this.contractorId) return;
    this.startLoading();

    try {
      if (!this.selectedPage?.id) {
        await this.updPages(this.contractorId);
      }

      if (this.selectedPage?.id) {
        this.wrContainer = document.getElementById('wr-container');
        await this.loadScript();
      }

      window.scrollTo(0, 0);
    } catch (error) {
      this.$buefy.snackbar.open({
        message: 'An unexpected error occurred, please contact support@enhancify.com',
        actionText: ' ',
        position: 'is-top',
        indefinite: true,
        type: 'is-danger',
      });
    }

    this.isActivated = true;
    this.finishLoading();
  },
};
</script>

<style lang="stylus" scoped>
.widgetReal
    min-height 36vw
  #wr-container
    opacity 1
    transition opacity 0.5s
  #wr-container.hidden
    opacity 0
    transition opacity 0.1s
  .choose.block-title
    margin-bottom 0.5rem
  .choose
    .title
      font-weight 400
      font-size 16px
      line-height 24px
      margin-top 30px
      margin-bottom 0
      color #969ea2
    .chooseOptions
      button
        background none
        width 54px
        height 54px
        border 1px solid rgba(143, 167, 179, 0.298039)
        border-radius 3px
        padding 10px
        cursor pointer
        margin-top 13.5px
        &:not(:last-child)
          margin-right 13.5px
        &.active,
        &:hover
          border-color #68BA62
        span
          display block
          width 100%
          height 100%
          border-radius 100%
    .chooseSwitch
      display flex
      margin-top 30px
      .chooseSwitchText
        margin-right 20px
    .buttons
      margin-top 35px
      button
        font-size 19px
        text-transform uppercase
        height 48px
        @media screen and (max-width: 499px)
          width 100%
  .fade-enter-active, .fade-leave-active
    transition opacity 0.1s
  .fade-enter, .fade-leave-to
    opacity 0
</style>
